import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { addLeaveLimit, updateLeaveLimit } from "../../redux/leaveLimitSlice";
import toast from "react-hot-toast";

const LeaveLimitModal = ({ isOpen, onClose, editItem }) => {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.leaveLimits);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (editItem) {
      setValue("leaveType", editItem.leaveType);
      setValue("maxDays", editItem.maxDays);
      setValue("isPaid", editItem.isPaid);
      setValue("description", editItem.description);
    } else {
      reset();
    }
  }, [editItem, setValue, reset]);

  const onSubmit = async (data) => {
    try {
      if (editItem) {
        await dispatch(updateLeaveLimit({ id: editItem.id, leaveLimitData: data })).unwrap();
        toast.success("Leave limit updated successfully!");
      } else {
        await dispatch(addLeaveLimit(data)).unwrap();
        toast.success("Leave limit added successfully!");
      }
      onClose();
      reset();
    } catch (error) {
      toast.error(error.message || "Failed to save leave limit");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {editItem ? "Edit Leave Limit" : "Add Leave Limit"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Leave Type</label>
            <input
              type="text"
              {...register("leaveType", { required: "Leave type is required" })}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="e.g., Annual Leave, Sick Leave"
            />
            {errors.leaveType && (
              <p className="text-red-500 text-xs mt-1">{errors.leaveType.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Maximum Days</label>
            <input
              type="number"
              {...register("maxDays", { 
                required: "Maximum days is required",
                min: { value: 0, message: "Days must be 0 or more" }
              })}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="e.g., 21"
            />
            {errors.maxDays && (
              <p className="text-red-500 text-xs mt-1">{errors.maxDays.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              {...register("description")}
              className="w-full border rounded px-3 py-2 text-sm"
              rows="3"
              placeholder="Description of the leave type..."
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register("isPaid")}
              className="mr-2"
            />
            <label className="text-sm">Paid Leave</label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#1a0f47] disabled:opacity-50"
            >
              {status === "loading" ? "Saving..." : editItem ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveLimitModal; 